import { computed, ref, watch } from 'vue';
import { equalP } from 'shared/lib/array/array';

export default function useLoader(
  loadLimit,
  goalFetcher,
  viewParamsService,
  debounce,
  selectedCycles,
  searchTerm,
  disabledGoalIds,
  baseFilter,
) {
  const unloadedRootItems = ref(0);
  // fetchLoading represents any call to getGoals
  const fetchLoading = ref(false);
  // viewChangedLoading represents a call to getGoals that is triggered by a change of filters, sorters, search (view related elements)
  const viewChangedLoading = ref(false);
  // listLoading represents the call to getGoals that are expected to change the collection
  const listLoading = ref(false);
  const retrieveGoals = ({ toggleViewChangedLoading = true, toggleListLoading = true } = { toggleViewChangedLoading: true, toggleListLoading: true }) => {
    if (toggleViewChangedLoading) {
      viewChangedLoading.value = true;
    }
    if (toggleListLoading) {
      listLoading.value = true;
    }
    fetchLoading.value = true;
    return goalFetcher.getGoals(
      {
        searchTerm: searchTerm.value,
        selectedCycles: selectedCycles.value,
        disabledGoalIds: disabledGoalIds.value,
      },
      baseFilter.value,
      {
        page: 1,
        itemsPerPage: loadLimit,
      },
    ).then(({ count }) => {
      unloadedRootItems.value = Math.max(0, count - loadLimit);
    }).finally(() => {
      fetchLoading.value = false;
      listLoading.value = false;
      viewChangedLoading.value = false;
    });
  };

  const viewChanged = (wait) => {
    debounce(retrieveGoals, wait, 'retrieveGoals');
  };
  const hasMore = computed(() => unloadedRootItems.value > 0);

  const loadMore = () => {
    if (!hasMore.value) {
      return;
    }

    retrieveGoals({ toggleViewChangedLoading: false });
  };

  watch(viewParamsService.applyFilterOnFirstLevelOnly, (val, oldVal) => {
    if (val === oldVal) {
      return;
    }
    viewChanged(1000);
  });

  watch(viewParamsService.gqlFilterObject, (val, oldVal) => {
    if (JSON.stringify(val) === JSON.stringify(oldVal)) {
      return;
    }
    viewChanged(1000);
  });
  watch(selectedCycles, (val, oldVal) => {
    if (equalP({ a: val, b: oldVal })) {
      return;
    }
    viewChanged(1000);
  });
  watch(searchTerm, (val, oldVal) => {
    if (JSON.stringify(val) === JSON.stringify(oldVal)) {
      return;
    }
    viewChanged(1000);
  });
  watch(viewParamsService.order, (val, oldVal) => {
    if (JSON.stringify(val) === JSON.stringify(oldVal)) {
      return;
    }
    viewChanged(1000);
  });

  return {
    fetchLoading,
    listLoading,
    viewChangedLoading,
    retrieveGoals,
    loadMore,
    hasMore,
  };
}
